import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Search, Star, Phone, Mail, MapPin, Plus } from "lucide-react";
import MessageButton from "@/components/MessageButton";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "beauty", label: "Beauty" },
  { id: "transport", label: "Transport" },
  { id: "home_services", label: "Home Services" },
  { id: "shops", label: "Shops" },
  { id: "repair", label: "Repair" },
  { id: "food", label: "Food & Drink" },
];

export default function Directory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: "", category: "beauty", description: "",
    contact_phone: "", contact_email: "", location: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/directory");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = "Service Directory · Jirani"; load(); }, []);

  const filtered = useMemo(() => {
    return items
      .filter((i) => cat === "all" || i.category === cat)
      .filter((i) =>
        !q.trim() ||
        i.business_name.toLowerCase().includes(q.toLowerCase()) ||
        (i.description || "").toLowerCase().includes(q.toLowerCase())
      );
  }, [items, cat, q]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.contact_phone) delete payload.contact_phone;
      const { data } = await api.post("/directory", payload);
      setItems([data, ...items]);
      setOpen(false);
      setForm({ business_name: "", category: "beauty", description: "", contact_phone: "", contact_email: "", location: "" });
      toast.success("Business added — asante!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="directory-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="jirani-eyebrow">Trusted by neighbors</div>
          <h1 className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
            <Wrench className="h-8 w-8 text-[#D95D39]" /> Service Directory
          </h1>
          <p className="mt-2 max-w-xl text-[#4A4A4A]">
            Find great fundis, salons, dukas, food and more — recommended right in your neighbourhood.
          </p>
        </div>

        {user && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-business-btn"
                className="jirani-pill rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
              >
                <Plus className="mr-2 h-4 w-4" /> Add business
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Outfit" }}>List a local business</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Business name</Label>
                  <Input data-testid="biz-name-input" required maxLength={140}
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className="mt-1 h-11 rounded-xl" />
                </div>
                <div>
                  <Label>Category</Label>
                  <select
                    data-testid="biz-category-select"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3"
                  >
                    {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input data-testid="biz-location-input" required
                    value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="mt-1 h-11 rounded-xl" placeholder="Estate, road, mall" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Phone (optional)</Label>
                    <Input data-testid="biz-phone-input" value={form.contact_phone}
                      onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                      className="mt-1 h-11 rounded-xl" placeholder="+254 ..." />
                  </div>
                  <div>
                    <Label>Email (optional)</Label>
                    <Input data-testid="biz-email-input" type="email" value={form.contact_email}
                      onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                      className="mt-1 h-11 rounded-xl" placeholder="hello@..." />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea data-testid="biz-description-input" required rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
                <DialogFooter>
                  <Button data-testid="biz-submit-btn" type="submit" disabled={saving}
                    className="jirani-pill w-full rounded-full bg-[#D95D39] text-white hover:bg-[#c25333]">
                    {saving ? "Adding..." : "Add to directory"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2E473B]/60" />
          <Input
            data-testid="directory-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search businesses"
            className="h-11 rounded-full border-black/10 bg-white pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`cat-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`jirani-pill rounded-full px-4 py-1.5 text-xs font-medium ${
                cat === c.id
                  ? "bg-[#2E473B] text-white"
                  : "border border-black/10 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-56 animate-pulse rounded-2xl border border-black/5 bg-white/60" />
          ))}
        </div>
      ) : (
        <div data-testid="directory-list" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((b) => (
            <article
              key={b.id}
              data-testid={`biz-${b.id}`}
              className="jirani-card flex flex-col rounded-2xl border border-black/5 bg-white p-5"
            >
              <div className="flex items-center justify-between">
                <Badge className="rounded-full bg-[#F4A261]/20 text-[#2E473B] hover:bg-[#F4A261]/20">
                  {(CATEGORIES.find((c) => c.id === b.category)?.label) || b.category}
                </Badge>
                <div className="inline-flex items-center gap-1 text-sm text-[#2E473B]">
                  <Star className="h-4 w-4 fill-[#F4A261] text-[#F4A261]" /> {b.rating?.toFixed(1) || "4.5"}
                </div>
              </div>

              <h3 className="mt-3 text-lg text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
                {b.business_name}
              </h3>
              <p className="mt-1 line-clamp-3 text-sm text-[#4A4A4A]">{b.description}</p>

              <div className="mt-4 space-y-1.5 text-xs text-[#2E473B]/85">
                <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {b.location}</div>
                {b.contact_phone && (
                  <div className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {b.contact_phone}</div>
                )}
                {b.contact_email && (
                  <div className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {b.contact_email}</div>
                )}
              </div>
              {b.author?.user_id && (
                <div className="mt-4">
                  <MessageButton peerId={b.author.user_id} peerName={b.author.name} />
                </div>
              )}
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]">
              No businesses match your search yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
